import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '../../lib/cn';
import { hideTabBarForPath } from './appNav';
import { PullToRefresh } from './PullToRefresh';
import { getScroll, saveScroll } from '../../lib/scrollRestoration';
import { useScreenLocation } from '../../lib/screenLocation';

// Each screen renders <AppShell> as its root: a single, self-contained scroll
// surface. The app frame around it — the persistent side nav (desktop), bottom
// tab bar (mobile) and the page-transition viewport — lives one level up in
// AppChrome (see App.tsx) and stays bolted down while this content slides. That
// split is what makes navigation feel native: the chrome never re-mounts or
// animates with the page, only the content does.
//
// The container owns its own momentum scroll + safe-area padding, clears the
// floating tab bar (unless this route hides it) so nothing hides behind it, and
// wires pull-to-refresh: dragging down from the top refetches whatever queries
// the current screen has mounted. Pass a custom `onRefresh` for a bespoke refresh,
// or `refreshable={false}` to opt out.
export function AppShell({
  children,
  refreshable = true,
  onRefresh,
  surfaceClassName,
  padBottom = true
}: {
  children: ReactNode;
  refreshable?: boolean;
  onRefresh?: () => Promise<unknown> | unknown;
  /** Override the scroll-surface background (also paints the safe-area top + the
   *  iOS rubber-band bounce). Defaults to the light paper world; an immersive dark
   *  screen (e.g. Make-Your-Picks) passes a dark fill so the notch + overscroll
   *  read native instead of flashing white. */
  surfaceClassName?: string;
  /** Opt out of the bottom safe-area / tab-bar padding when the screen pins its own
   *  bottom bar that already owns the safe inset (e.g. Make-Your-Picks). Avoids a
   *  double home-indicator gap under a sticky CTA. */
  padBottom?: boolean;
}) {
  const { pathname, key } = useScreenLocation();
  const hideTabBar = hideTabBarForPath(pathname);
  const qc = useQueryClient();
  const handleRefresh = onRefresh ?? (() => qc.refetchQueries({ type: 'active' }));

  // Restore the scroll offset when this history entry comes back (a pop), and
  // keep it remembered as the user scrolls. Forward navigations get a fresh key,
  // so they correctly start at the top. We save on scroll (not on unmount) so the
  // StrictMode mount→unmount→mount cycle can't clobber a real offset with 0
  // before the restore frame runs.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = getScroll(key);
    let raf = 0;
    if (saved && saved > 0) {
      // Two frames so the page has laid out (and any synchronous data is in)
      // before we jump — avoids the offset clamping to a not-yet-tall page.
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          el.scrollTop = saved;
        });
      });
    }
    const onScroll = () => saveScroll(key, el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [key]);

  return (
    <PullToRefresh
      enabled={refreshable}
      onRefresh={handleRefresh}
      containerRef={scrollRef}
      className={cn(
        'native-scroll no-scrollbar relative h-full overflow-y-auto pt-safe md:pt-0',
        surfaceClassName ?? 'bg-white md:bg-canvas',
        !padBottom ? 'md:pb-0' : hideTabBar ? 'pb-safe md:pb-0' : 'pb-tab-bar md:pb-0'
      )}
    >
      {children}
    </PullToRefresh>
  );
}

/** Shared horizontal frame for league hub + pick flows — OS gutters on mobile, centred
 *  wide column on desktop. Page-level content sits flush in this column; `px-[18px]`
 *  is only for padding *inside* a card (see BuildHeader), never stacked on top. */
export const SCREEN_COLUMN =
  'mx-auto w-full px-safe md:max-w-none md:px-8 lg:max-w-5xl lg:px-10';

/** Wider column for multi-pane screens — same mobile as the default; tablet (`md`–`lg`)
 *  uses the full content width beside the icon rail; real desktops grow further. */
export const SCREEN_COLUMN_WIDE =
  'mx-auto w-full px-safe md:max-w-none md:px-8 lg:max-w-6xl lg:px-10 xl:max-w-7xl';

/** Near-full-bleed column for boards/tables that want maximum horizontal room on
 *  desktop. Mobile is untouched; tablet releases the cap; `lg+` goes full-bleed. */
export const SCREEN_COLUMN_FULL =
  'mx-auto w-full px-safe md:max-w-none md:px-8 lg:max-w-none lg:px-12 xl:px-16';

const COLUMN_BY_WIDTH = {
  default: SCREEN_COLUMN,
  wide: SCREEN_COLUMN_WIDE,
  full: SCREEN_COLUMN_FULL
} as const;

/** Full-bleed on mobile (only OS safe gutters), wide centered column on desktop.
 *  `width` only changes the desktop (`lg+`) max-width — mobile is identical across
 *  all variants; the tablet band (`md`–`lg`) uses full width beside the icon rail. */
export function Content({
  children,
  width = 'default'
}: {
  children: ReactNode;
  width?: keyof typeof COLUMN_BY_WIDTH;
}) {
  return (
    <div className={cn(COLUMN_BY_WIDTH[width], 'flex min-h-full flex-col pb-6 md:pb-16 md:pt-2')}>
      {children}
    </div>
  );
}
